/* 해설과 문항의 형식. 데모와 MCP 가 같은 문장을 쓴다 */

export const SOLUTION_FORMAT = {
  problem: '# 문제',
  figure: '# 그림',
  steps: '# 정석 풀이 (Track A)',
  quick: '# 실전 풀이 (Track B)',
  plan: '# 그림 계획',
  stepsRule: '정석 풀이(Track A) 단계 2~8개: 개념을 잡는 순서(도함수를 구한다 → f\'(x)=0 인 점에서 극값을 분석한다 → 조건을 연립해 미지수를 정한다 …). 각 단계는 한국어 한두 문장, 수식은 $…$ 로 감싸고 무엇을 구했는지 값으로 끝낸다.',
  quickRule: '실전 풀이(Track B) 1~3단계: 시험장에서 바로 쓰는 빠른 길(공식·대칭·특수값).',
  verifyNone: '정답 미검증. 도구가 잰 값(넓이, 길이)만 참고. AI 풀이는 사람이 확인해야 한다',
};

/** 호스트 모델이 글을 쓸 때의 작문 규칙(사용자 지시 2026-10-20). 풀이 글과 단계 설명이 같은 규칙을 받는다 */
export const WRITING_RULE = '작문 규칙: 한국어 평서문으로 쓰고 한 단계는 한두 문장이다. 수식은 $…$ 안에 LaTeX 로만 쓴다. 가운뎃점(·), 이모지, 유니코드 수학 기호(−, ∣, ⋅, ⟹, 𝑥 같은 수학 알파벳), 전각 문자, 중국어, 영어 설명을 쓰지 않는다. 빼기는 -, 절댓값은 |x| 를 $ 안에 쓴다. 기호 표기는 기출 발문을 따르고 눈에 바로 보이게 쓴다. 수의 곱은 \\times 로 드러내 쓴다(5\\times 8\\times\\cos 60^\\circ, 2\\times\\pi). \\cdot 은 벡터의 내적(\\overrightarrow{AB}\\cdot\\overrightarrow{AC})에만 쓴다. 나눗셈은 \\frac, 근호는 \\sqrt, 부등호는 \\le \\ge \\neq, 선분 길이는 \\overline{AB}, 각은 \\angle, 도는 ^\\circ, 함수와 상수는 \\sin \\cos \\tan \\ln \\log \\pi 명령으로 쓴다. /, *, <=, >=, !=, sqrt(), sin(), pi, ° 같은 계산기 표기와 곱 기호 생략(5 8)을 쓰지 않는다.';

/** 풀이 글을 절로 가른다: 정석 풀이(Track A), 실전 풀이(Track B). 「그림 계획」 절은 풀이가 아니므로 두 절에서 뺀다. 절 머리가 하나도 없으면 글 전체가 정석 풀이다 */
export function splitWritten(text: string): { steps: string; quick: string } {
  const a = text.indexOf(SOLUTION_FORMAT.steps);
  const b = text.indexOf(SOLUTION_FORMAT.quick);
  const p = text.indexOf(SOLUTION_FORMAT.plan);
  if (a < 0 && b < 0) return { steps: (p >= 0 ? text.slice(0, p) : text).trim(), quick: '' };
  const after = (at: number): number => { const nl = text.indexOf('\n', at); return nl < 0 ? text.length : nl + 1; };
  const end = (at: number): number => [a, b, p].filter((x) => x > at).sort((x, y) => x - y)[0] ?? text.length;
  const steps = a >= 0 ? text.slice(after(a), end(a)) : text.slice(0, b);
  const quick = b >= 0 ? text.slice(after(b), end(b)) : '';
  return { steps: steps.trim(), quick: quick.trim() };
}

/** 풀이 글의 「그림 계획」 절. 없으면 빈 글자 */
export function planOf(text: string): string {
  const p = text.indexOf(SOLUTION_FORMAT.plan);
  return p < 0 ? '' : text.slice(p + SOLUTION_FORMAT.plan.length).trim();
}

/** 문항을 새로 만들 때의 형식. 사용자 지시(2026-10-14): 요청에 「객관식」이 없으면 단답형이 기본이다 */
export const ITEM_FORMAT_RULE = '- 문항을 새로 만들 때 요청에 「객관식」이 없으면 단답형으로 만든다. 단답형에는 선지(①~⑤)를 만들지 않고, 답이 0 이상 999 이하의 정수가 되게 수를 고른다(수능 단답형 형식, 끝 관문이 검사한다). 「객관식」이 있을 때만 선지 다섯 개를 만든다.';

/** 레퍼런스 03 「AI 예상 문제」의 변형 요소(숫자, 조건, 함수를 변형 / 함수 계수 변경 + 조건 교체) */
export const VARIANT_ELEMENTS = ['숫자', '함수 계수', '조건', '함수', '기술 결합'] as const;

/** 정석 풀이 단계의 그림 선언. 단계 줄 끝의 「[그림: 이름, 이름]」. 그 단계가 처음 세우는 개체의 이름이고, 코드가 단계 ↔ 삽화 매핑을 이것으로 결정한다(사용자 지시 2026-10-21) */
export const FIGURE_DECLARATION_RULE = '각 단계 줄 끝에 「[그림: 이름, 이름]」을 붙인다. 그 단계에서 처음 세우는 개체(점, 선, 접선, 다각형, 측정)의 이름이고 뒤의 그리기 호출에서 같은 이름을 쓴다. 그 단계에 새로 그릴 것이 없으면 「[그림: 없음]」. 이름은 문항이 준 이름(A, B, P, f)이거나 짧은 이름(l1, m1, S1)이다.';
export const FIGURE_DECLARATION = /\s*\[그림:\s*([^\]]*)\]\s*$/;

/** 단계 줄에서 그림 선언을 읽고 뗀다. 번호 줄(「1. 」)마다 선언한 이름 배열. 「없음」이나 빈 선언은 빈 배열 */
export function readDeclarations(steps: string): { text: string; declared: string[][] } {
  const declared: string[][] = [];
  const lines = steps.split('\n').map((line) => {
    if (!/^\s*\d+\.\s/.test(line)) return line;
    const m = FIGURE_DECLARATION.exec(line);
    if (!m) { declared.push([]); return line; }
    const names = m[1].split(/[,，\s]+/).map((n) => n.trim()).filter((n) => n && n !== '없음');
    declared.push(names);
    return line.replace(FIGURE_DECLARATION, '');
  });
  return { text: lines.join('\n'), declared };
}
